/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Placeholder estructural del panel — nombres de sucursal reales, números en skeleton.
 */
import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, Text, View } from 'react-native';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import type { BranchAccess } from '../../shell/session/store/sessionTypes';

function SkeletonBar({
  width,
  height,
  radius = 6,
  style,
}: {
  width: number | `${number}%`;
  height: number;
  radius?: number;
  style?: object;
}) {
  const pulse = useRef(new Animated.Value(0.45)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 0.85,
          duration: 720,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.45,
          duration: 720,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  return (
    <Animated.View
      style={[
        styles.bar,
        { width, height, borderRadius: radius, opacity: pulse },
        style,
      ]}
    />
  );
}

function BranchCardSkeleton({
  name,
  showPendingOrders,
  showLowStock,
}: {
  name: string;
  showPendingOrders: boolean;
  showLowStock: boolean;
}) {
  return (
    <View style={styles.branchCard}>
      <View style={styles.branchTop}>
        <View style={styles.branchTitleWrap}>
          <Text style={styles.branchName}>{name}</Text>
          <SkeletonBar width="72%" height={12} />
        </View>
        <View style={styles.branchRight}>
          <SkeletonBar width={72} height={18} />
          <SkeletonBar width={88} height={22} radius={999} />
        </View>
      </View>
      <View style={styles.stats}>
        {showPendingOrders ? (
          <View style={styles.stat}>
            <SkeletonBar width={20} height={14} />
            <SkeletonBar width="80%" height={10} style={styles.statLabelBar} />
          </View>
        ) : null}
        {showLowStock ? (
          <View style={styles.stat}>
            <SkeletonBar width={20} height={14} />
            <SkeletonBar width="80%" height={10} style={styles.statLabelBar} />
          </View>
        ) : null}
      </View>
    </View>
  );
}

type Props = {
  branches: BranchAccess[];
  organizationName: string;
  wide?: boolean;
  showPendingOrders: boolean;
  showLowStock: boolean;
};

export function OwnerPanelLoadingSkeleton({
  branches,
  organizationName,
  wide = false,
  showPendingOrders,
  showLowStock,
}: Props) {
  return (
    <View style={styles.wrap} testID="owner-panel.loading" accessibilityLabel="Cargando panel">
      <View style={styles.totalCard}>
        <Text style={styles.totalLabel}>Total {organizationName}</Text>
        <SkeletonBar width={160} height={wide ? 34 : 28} style={styles.totalValueBar} />
        <SkeletonBar width="55%" height={13} />
      </View>

      <Text style={styles.sectionLabel}>Por sucursal · elegí un local</Text>

      <View style={styles.branchGrid}>
        {branches.map((branch) => (
          <View key={branch.id} style={wide ? styles.branchSlotWide : styles.branchSlotFull}>
            <BranchCardSkeleton
              name={branch.name}
              showPendingOrders={showPendingOrders}
              showLowStock={showLowStock}
            />
          </View>
        ))}
      </View>

      <Text style={styles.sectionLabel}>Alertas</Text>
      <View style={styles.alertSkeleton}>
        <SkeletonBar width="88%" height={14} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  bar: {
    backgroundColor: 'rgba(28, 25, 23, 0.08)',
  },
  totalCard: {
    ...ticketSurfaces.paperSheet,
    padding: 14,
    gap: 8,
  },
  totalLabel: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: colors.textMuted,
  },
  totalValueBar: {
    marginTop: 2,
  },
  sectionLabel: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  branchGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignItems: 'stretch',
  },
  branchSlotFull: {
    width: '100%',
  },
  branchSlotWide: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '30%',
    minWidth: 240,
  },
  branchCard: {
    ...ticketSurfaces.paperSheet,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 10,
    gap: 10,
    height: '100%',
  },
  branchTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  branchTitleWrap: { flex: 1, minWidth: 0, gap: 6 },
  branchName: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 14,
    color: colors.textPrimary,
  },
  branchRight: {
    alignItems: 'flex-end',
    gap: 6,
  },
  stats: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 6,
  },
  stat: {
    flexGrow: 1,
    flexShrink: 1,
    backgroundColor: 'rgba(28, 25, 23, 0.03)',
    borderRadius: 10,
    padding: 8,
    gap: 6,
  },
  statLabelBar: {
    marginTop: 2,
  },
  alertSkeleton: {
    ...ticketSurfaces.paperSheet,
    borderLeftWidth: 3,
    borderLeftColor: 'rgba(28, 25, 23, 0.12)',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
});
